import { PropertyTypes } from "../../../../enums/PropertyTypes";
import { ISchema, ISchemaAction, ISchemaChild, ISchemaFilter, ISchemaProperty } from "../Schema";
import { FilterTypes } from "../../../../enums/FilterTypes";
import { FilterMatchType } from "../../../../enums/FilterMatchType";
import { EntityNames } from "../../../../enums/EntityNames";
import { EntityViewTypes } from "../../../../enums/EntityViewTypes";
import { ActionTypes } from "../../../../enums/ActionTypes";
import { ActionStyles } from "../../../../enums/ActionStyles";
import editIcon from "../../../../assets/icons/icon-pen-white.svg"
import trashIcon from "../../../../assets/icons/icon-bin.svg"
import { NotEmptyString, ValidAddress, ValidateEmail, ValidatePhoneNumber } from "../../../../Validation/Validation";
import SidePanelAccounts from "../../layout/SidePanel/layouts/SidePanelAccounts";
import { ChartType } from "../../Charts/ChartType";
import { hasAccountBasedPermission, hasPermission } from "../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../modules/auth/Permissions/PermissionStrings";


export const AccountSchema: ISchema = {
    displayName: "Accounts",
    GetSidePanelComponent: (data, onViewPage) => <SidePanelAccounts data={data} onViewPage={onViewPage} />,
    props: [
        {
            name: 'id',
            type: PropertyTypes.string,
            key: true
        } as ISchemaProperty,
        {
            name: 'name',
            displayName: 'Account name',
            descriptor: true,
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: true,
            showInView: false,
            requiredInForm: true,
            validateRule: NotEmptyString as Function,
            validationError: "Name cannot be empty",
            autocomplete: "organization",
        } as ISchemaProperty,
        {
            name: 'number',
            displayName: 'ID number',
            type: PropertyTypes.string,
            key: false,
            showInList: true,
            showInForm: true,
            showInView: false,
            requiredInForm: true,
        } as ISchemaProperty,
        {
            name: 'activeUsersCount',
            displayName: 'Number of active users',
            type: PropertyTypes.number,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'status',
            displayName: 'Status',
            type: PropertyTypes.status,
            key: false,
            showInList: true,
            showInForm: false,
            showInView: false,
            requiredInForm: false,
        } as ISchemaProperty,
        {
            name: 'address',
            displayName: 'Address',
            type: PropertyTypes.address,
            key: false,
            showInList: false,
            showInForm: true,
            group: 0,
            showInView: false,
            requiredInForm: true,
            validateRule: ValidAddress as Function
        } as ISchemaProperty,
        {
            name: 'contactDetails',
            displayName: 'Contact Details',
            type: PropertyTypes.contactDetails,
            key: false,
            showInList: false,
            showInForm: true,
            group: 1,
            showInView: false,
            requiredInForm: false,
            children: [
                {
                    name: "telephone",
                    validateRule: ValidatePhoneNumber as Function,
                    validationError: "Please enter valid phone number",
                    autocomplete: "tel",
                } as ISchemaProperty,
                {
                    name: "mobile",
                    validateRule: ValidatePhoneNumber as Function,
                    validationError: "Please enter valid phone number",
                    autocomplete: "tel",
                } as ISchemaProperty,
                {
                    name: "fax",
                    autocomplete: "tel",
                } as ISchemaProperty,
                {
                    name: "email",
                    validateRule: ValidateEmail as Function,
                    validationError: "Please enter valid email",
                    autocomplete: "email",
                } as ISchemaProperty,
            ]
        } as ISchemaProperty,
    ],

    primaryFilter: {
        PropertyName: "Name",
        displayName: "Search",
        type: FilterTypes.search,
        required: false,
        className: "width--full",
        matchType: FilterMatchType.Substring,
        propertyType: PropertyTypes.string,
        placeHolder: "Enter an account name"
    } as ISchemaFilter,

    filters: [],

    children: [
        { 
            entityName: EntityNames.storageSummary, 
            displayName: "Storage Summary", 
            cantView: true, 
            viewType: EntityViewTypes.storageSummaryChart, 
            dataType: ChartType.Bar, 
            enableFilters: true, 
            navPropertyName: "AccountId", 
            canAdd: false,
            permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_Storage_Summary))
        } as ISchemaChild,
        { 
            entityName: EntityNames.users, 
            displayName: "Users", 
            cantView: false, 
            viewType: EntityViewTypes.list, 
            enableFilters: true, 
            navPropertyName: "AccountId", 
            canAdd: ((ctx, accountId) => hasAccountBasedPermission(accountId, ctx.permissions, PermissionTypes.write, PermissionConcerns.Customer_Account_Users, ctx.cellTrakOperatorClaim) || hasAccountBasedPermission(accountId, ctx.permissions, PermissionTypes.write, PermissionConcerns.Add_Account, ctx.cellTrakOperatorClaim)),
            permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_Users))
        } as ISchemaChild,
        { 
            entityName: EntityNames.accountAddresses, 
            displayName: "Address book", 
            cantView: false, 
            viewType: EntityViewTypes.addressBook, 
            enableFilters: true, 
            navPropertyName: "AccountId", 
            canAdd: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.write, PermissionConcerns.Manage_Account_Address_Book)),
            permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_Address_Book))
        } as ISchemaChild,
        { 
            entityName: EntityNames.accountInvoices, 
            displayName: "Invoices", 
            cantView: true, 
            viewType: EntityViewTypes.list, 
            enableFilters: true, 
            navPropertyName: "AccountId", 
            canAdd: false,
            permissionToView: ((ctx, accountId) => hasAccountBasedPermission(accountId, ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_Invoices, ctx.cellTrakOperatorClaim))
        } as ISchemaChild,
        { 
            entityName: EntityNames.costMultipliers, 
            displayName: "Cost Multipliers", 
            cantView: true, 
            viewType: EntityViewTypes.writableList, 
            enableFilters: false, 
            navPropertyName: "AccountId", 
            canAdd: false,
            permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_Cost_Multipliers))
        } as ISchemaChild,
        { 
            entityName: EntityNames.accountOrderNumbers, 
            displayName: "Order Numbers", 
            cantView: false, 
            viewType: EntityViewTypes.writableList, 
            enableFilters: true, 
            navPropertyName: "AccountId", 
            canAdd: ((ctx, accountId) => hasAccountBasedPermission(accountId, ctx.permissions, PermissionTypes.write, PermissionConcerns.Manage_Account_OrderNumbers, ctx.cellTrakOperatorClaim)),
            permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_OrderNumbers))
        } as ISchemaChild,
        { 
            entityName: EntityNames.prefixes, 
            displayName: "Prefixes", 
            cantView: false, 
            viewType: EntityViewTypes.writableList, 
            enableFilters: true, 
            navPropertyName: "AccountId", 
            canAdd: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.write, PermissionConcerns.Manage_Account_Prefixes)),
            permissionToView: ((ctx) => hasPermission(ctx.permissions, PermissionTypes.read, PermissionConcerns.Account_Prefixes))
        } as ISchemaChild,
    ],

    sidePanelActions: [
        { text: "Edit Details", action: ActionTypes.edit, style: ActionStyles.primary, icon: editIcon } as ISchemaAction,
        { text: "Delete Account", action: ActionTypes.delete, style: ActionStyles.destructive, icon: trashIcon } as ISchemaAction,
    ]
}
